'use client'

import Heading from '@/components/Heading/Heading'
import SearchBar from '@/components/UiElements/SearchBar'
import { useCallback, useEffect, useMemo, useState } from 'react'
import HelpCategoryContent from './HelpCategoryContent'
import { HelpCategory, HelpSection, helpContent } from './helpContent'
import TutorialPlayerModal from './TutorialPlayerModal'
import TutorialsPage from './TutorialsPage'
import { HelpTutorial, useTutorials } from './tutorials'
import GetStartedPage from '@/features/onboarding/GetStartedPage'
import { useOnboarding } from '@/features/onboarding/OnboardingProvider'

const normalize = (value: string) => value.trim().toLowerCase()

type HelpTabId = 'get-started' | 'guides' | 'tutorials'

const HelpGuides = () => {
  // Subscribe so guide-embedded tutorial cards refresh when the remote config loads.
  useTutorials()
  const [searchText, setSearchText] = useState('')
  const [activeContentId, setActiveContentId] = useState('help-category-welcome')
  const [selectedTutorial, setSelectedTutorial] = useState<HelpTutorial | null>(null)
  const query = normalize(searchText)
  const closeTutorial = useCallback(() => setSelectedTutorial(null), [])

  useEffect(() => {
    const hashId = decodeURIComponent(window.location.hash.replace(/^#/, ''))
    if (!hashId) return

    const scrollTimer = window.setTimeout(() => {
      const target = document.getElementById(hashId)
      if (!target) return

      setActiveContentId(hashId)
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)

    return () => window.clearTimeout(scrollTimer)
  }, [])

  const scrollToContent = (id: string) => {
    setActiveContentId(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const filteredContent = useMemo(() => {
    if (!query) return helpContent

    return helpContent
      .map((category) => {
        const categoryMatch = normalize(category.title).includes(query)
        const sections = category.sections
          .map((section) => {
            const sectionMatch = normalize(section.title).includes(query)
            const topics =
              categoryMatch || sectionMatch
                ? section.topics
                : section.topics.filter((topic) => normalize(topic.title).includes(query))

            return categoryMatch || sectionMatch || topics.length > 0 ? { ...section, topics } : null
          })
          .filter(Boolean) as HelpSection[]

        return categoryMatch || sections.length > 0 ? { ...category, sections } : null
      })
      .filter(Boolean) as HelpCategory[]
  }, [query])

  useEffect(() => {
    const contentIds = filteredContent.flatMap((category) => [
      `help-category-${category.id}`,
      ...category.sections.map((section) => `help-section-${section.id}`),
    ])

    if (contentIds.length === 0) {
      return
    }

    let animationFrameId: number | null = null

    const updateActiveContent = () => {
      const navigationOffset = 176
      let currentId = contentIds[0]

      contentIds.forEach((id) => {
        const element = document.getElementById(id)
        if (element && element.getBoundingClientRect().top <= navigationOffset) {
          currentId = id
        }
      })

      setActiveContentId((activeId) => (activeId === currentId ? activeId : currentId))
    }

    const scheduleActiveContentUpdate = () => {
      if (animationFrameId !== null) return

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null
        updateActiveContent()
      })
    }

    scheduleActiveContentUpdate()
    window.addEventListener('scroll', scheduleActiveContentUpdate, { passive: true })
    window.addEventListener('resize', scheduleActiveContentUpdate)

    return () => {
      if (animationFrameId !== null) window.cancelAnimationFrame(animationFrameId)
      window.removeEventListener('scroll', scheduleActiveContentUpdate)
      window.removeEventListener('resize', scheduleActiveContentUpdate)
    }
  }, [filteredContent])

  return (
    <div className="pr-xxl pb-xxl flex flex-col gap-xxl">
      <TutorialPlayerModal tutorial={selectedTutorial} onClose={closeTutorial} />

      <SearchBar placeholder="Search help titles" searchText={searchText} onChange={setSearchText} />

      {filteredContent.length > 0 ? (
        <div className="flex flex-row-reverse items-start gap-xxl">
          <aside className="sticky top-[100px] self-start w-[240px] shrink-0 max-h-[calc(100vh-120px)] overflow-y-auto border-l border-gray-200 pl-md">
            <nav aria-label="Help contents" className="flex flex-col gap-md">
              {filteredContent.map((category) => (
                <div key={category.id} className="flex flex-col gap-xs">
                  <button
                    type="button"
                    onClick={() => scrollToContent(`help-category-${category.id}`)}
                    aria-current={activeContentId === `help-category-${category.id}` ? 'location' : undefined}
                    className={`rounded-sm px-sm py-xs text-left text-sm font-semibold transition-colors ${
                      activeContentId === `help-category-${category.id}`
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-primary-700'
                    }`}
                  >
                    {category.title}
                  </button>
                  <div className="flex flex-col gap-xs">
                    {category.sections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => scrollToContent(`help-section-${section.id}`)}
                        aria-current={activeContentId === `help-section-${section.id}` ? 'location' : undefined}
                        className={`rounded-sm px-sm py-xs text-left text-sm transition-colors ${
                          activeContentId === `help-section-${section.id}`
                            ? 'bg-primary-50 font-medium text-primary-700'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-primary-700'
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </aside>

          <div className="min-w-0 flex-1 flex flex-col gap-xxxl">
            {filteredContent.map((category) => (
              <HelpCategoryContent
                key={category.id}
                category={category}
                withAnchors
                stickyHeader
                onPlayTutorial={setSelectedTutorial}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-row justify-center items-center px-mx py-xl text-gray-300 bg-gray-50 rounded-md gap-lg h-[150px]">
          No help topics match your search.
        </div>
      )}
    </div>
  )
}

export default function HelpPage() {
  const [selectedTabId, setSelectedTabId] = useState<HelpTabId>('get-started')
  const { progress, isComplete } = useOnboarding()
  const helpTabs = useMemo(() => {
    const getStartedTab = { id: 'get-started' as const, name: `Get Started (${progress}%)` }
    const standardTabs = [
      { id: 'guides' as const, name: 'Guides' },
      { id: 'tutorials' as const, name: 'Tutorials' },
    ]

    return isComplete ? [...standardTabs, getStartedTab] : [getStartedTab, ...standardTabs]
  }, [progress, isComplete])
  const selectedTab = Math.max(
    helpTabs.findIndex((tab) => tab.id === selectedTabId),
    0
  )

  return (
    <div className="flex w-full bg-white gap-md min-h-full">
      <div className="flex-grow flex flex-col">
        <Heading
          title="Help"
          description="Short guides and tutorials for common admin tasks."
          tab={{
            tabs: helpTabs,
            selectedTab,
            onSelectedChange: (tabIndex) => setSelectedTabId(helpTabs[tabIndex].id),
          }}
        />

        {selectedTabId === 'get-started' && <GetStartedPage embedded />}
        {selectedTabId === 'guides' && <HelpGuides />}
        {selectedTabId === 'tutorials' && <TutorialsPage />}
      </div>
    </div>
  )
}
